import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import api from "../../services/api";
import refreshToken from "../../utils/refreshToken";
import formatValue from "../../utils/formatValue";
import { Link, useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { useAuth } from "../../hooks/auth";
import Layout from "../../components/Layout";
import Loading from "../../components/Loading";
import ModalConfirmRemoveProperty from "../../components/ModalConfirmRemoveProperty";
import ModalConfirmCloneProperty from "../../components/ModalConfirmCloneProperty";
import { ListPagination } from "../../components/ListPagination";
import { useToast } from "../../hooks/toast";
import { useRefresh } from "../../hooks/refresh";
import {
  LIST_SEARCH_DEBOUNCE_MS,
  shouldSyncSearchToFirstPage,
} from "../../utils/listFilterQuery";
import { PropertiesListFilters } from "./PropertiesListFilters";
import {
  buildPropertiesApiParams,
  buildPropertiesPath,
  hasActivePropertyListFilters,
  parsePropertyListFiltersFromSearch,
  PropertyListFilters,
} from "./propertiesListQuery";
import {
  buildPropertyAddressPrimaryLine,
  formatAreaForList,
  formatCepLine,
  formatCountForList,
  getPropertyTypeLabel,
  toPropertyListRowFields,
} from "../../utils/propertyListRowFormat";
import { parseParentHasAvailableChildrenError } from "../../utils/propertyChildren";
import { deletePropertyWithChildrenConfirm } from "../../services/propertyChildren";
import {
  buildKeyLocationPatchBody,
  isDigitalKeyLocation,
  validateKeyLocationNoteForOthers,
  validateKeyLocationPasswordForDigital,
} from "../../utils/keyLocationRules";
import {
  fetchKeyLocations,
  isDigitalKeyLocationItem,
  patchPropertyKeyLocation,
  type KeyLocationCatalogItem,
} from "../../services/propertyKeyLocation";
import {
  readListingKeyLocationPassword,
  readPropertyKeyLocationPassword,
} from "../../utils/visitKeyLocation";
import PropertyKeyLocationTableSelect, {
  type KeyLocationTableSavePayload,
} from "../../components/PropertyKeyLocationTableSelect";
import {
  isDraftStatus,
  PROPERTY_STATUS_LABEL,
  PropertyStatus,
} from "../../global/propertyStatus";
import {
  commissionFormFromApi,
  formatCommissionSummary,
} from "../../utils/propertyCommission";

import { FiEye } from "react-icons/fi";
import { BiBed, BiBath, BiCar, BiShapeSquare, BiEdit, BiTrash, BiCopy } from "react-icons/bi";

interface IProperties {
  id: number;
  title?: string;
  is_rented: number;
  rent_value: string;
  address: string;
  neighborhood: string;
  number: string;
  floor?: string | null;
  complement?: string | null;
  cep?: string | null;
  zip_code?: string | null;
  rooms?: number | null;
  bathrooms?: number | null;
  garages?: number | null;
  area?: number | string | null;
  property_type?: { name?: string } | string | null;
  type_name?: string | null;
  property_type_name?: string | null;
  key_location?: { id: number; name: string; slug?: string } | null;
  key_location_id?: number | null;
  key_location_note?: string | null;
  key_location_password?: string | null;
  status?: string | null;
  status_name?: string | null;
  is_draft?: boolean;
  commission_type?: string | null;
  admin_percentage?: number | string | null;
  first_rent_commission_7cantos_percentage?: number | string | null;
  partner_name?: string | null;
  partner_first_rent_commission_percentage?: number | string | null;
  commission_estimate?: unknown;
}

interface IParams {
  page: string;
}

const Properties: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { params } = useRouteMatch<IParams>();

  const { dateToExpires, access_token, user } = useAuth();
  const { addToast } = useToast();
  const { refresh, handleRefresh } = useRefresh();

  const isPartnerBroker = user?.role === "partner_broker";
  const currentPage = Number(params.page) || 1;

  const filters = useMemo(
    () =>
      parsePropertyListFiltersFromSearch(location.search, {
        includeConsultant: !isPartnerBroker,
      }),
    [location.search, isPartnerBroker]
  );

  const [searchTerm, setSearchTerm] = useState(filters.search);
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  const [properties, setProperties] = useState<IProperties[]>([]);
  const [lastPage, setLastPage] = useState<number>(0);
  const [loadingApiProperties, setLoadingApiProperties] = useState(true);
  const [propertyIdToRemove, setPropertyIdToRemove] = useState(0);
  const [propertyIdToClone, setPropertyIdToClone] = useState(0);
  const [openModalConfirmRemoveProperty, setOpenModalConfirmRemoveProperty] =
    useState(false);
  const [removeRequiresChildrenConfirm, setRemoveRequiresChildrenConfirm] =
    useState(false);
  const [openModalConfirmCloneProperty, setOpenModalConfirmCloneProperty] =
    useState(false);
  const [
    loadingApiRequestToRemoveProperty,
    setLoadingApiRequestToRemoveProperty,
  ] = useState(false);
  const [
    loadingApiRequestToCloneProperty,
    setLoadingApiRequestToCloneProperty,
  ] = useState(false);
  const [loadApiError, setLoadApiError] = useState("");
  const [listRefreshing, setListRefreshing] = useState(false);
  const hasLoadedListOnceRef = useRef(false);
  const [keyLocationCatalog, setKeyLocationCatalog] = useState<
    KeyLocationCatalogItem[]
  >([]);
  const [savingKeyLocationId, setSavingKeyLocationId] = useState<number | null>(
    null
  );

  useEffect(() => {
    setSearchTerm(filters.search);
  }, [filters.search]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, LIST_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    if (!shouldSyncSearchToFirstPage(debouncedSearch, filters.search)) return;

    const nextUrl = buildPropertiesPath(1, {
      ...filters,
      search: debouncedSearch,
    });
    const currentUrl = `${history.location.pathname}${history.location.search}`;
    if (currentUrl !== nextUrl) history.replace(nextUrl);
  }, [debouncedSearch, filters, history]);

  const applyFilters = useCallback(
    (nextFilters: PropertyListFilters, page = 1) => {
      const nextUrl = buildPropertiesPath(page, nextFilters);
      const currentUrl = `${history.location.pathname}${history.location.search}`;
      if (currentUrl !== nextUrl) history.replace(nextUrl);
    },
    [history]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [params.page]);

  useEffect(() => {
    async function loadCatalog() {
      if (!user) return;
      try {
        const token = await refreshToken(dateToExpires, access_token);
        api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;
        setKeyLocationCatalog(await fetchKeyLocations(api));
      } catch {
        setKeyLocationCatalog([]);
      }
    }
    loadCatalog();
  }, [user, dateToExpires, access_token]);

  const handleSaveKeyLocation = useCallback(
    async (propertyId: number, payload: KeyLocationTableSavePayload) => {
      const { keyLocationId, keyLocationNote, keyLocationPassword } = payload;
      const currentProperty = properties.find((item) => item.id === propertyId);
      const currentLocationId =
        currentProperty?.key_location &&
        typeof currentProperty.key_location === "object" &&
        currentProperty.key_location.id != null
          ? Number(currentProperty.key_location.id)
          : currentProperty?.key_location_id != null
            ? Number(currentProperty.key_location_id)
            : null;
      const noteErr = validateKeyLocationNoteForOthers(
        keyLocationCatalog,
        keyLocationId,
        keyLocationNote
      );
      if (noteErr) {
        addToast({ title: noteErr, type: "error" });
        return;
      }
      const passwordErr = validateKeyLocationPasswordForDigital(
        keyLocationCatalog,
        keyLocationId,
        keyLocationPassword,
        {
          allowBlankToKeep: isDigitalKeyLocation(
            keyLocationCatalog,
            currentLocationId
          ),
        }
      );
      if (passwordErr) {
        addToast({ title: passwordErr, type: "error" });
        return;
      }
      setSavingKeyLocationId(propertyId);
      try {
        const token = await refreshToken(dateToExpires, access_token);
        api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;
        await patchPropertyKeyLocation(
          api,
          propertyId,
          buildKeyLocationPatchBody(keyLocationCatalog, {
            keyLocationId,
            keyLocationNote,
            keyLocationPassword,
          })
        );
        const catEntry = keyLocationId == null ? null : keyLocationCatalog.find((c) => c.id === keyLocationId) ?? null;
        const nextPassword =
          catEntry && isDigitalKeyLocationItem(catEntry)
            ? String(keyLocationPassword ?? "").trim() ||
              readPropertyKeyLocationPassword(currentProperty)
            : "";
        setProperties((prev) =>
          prev.map((p) =>
            p.id === propertyId
              ? {
                  ...p,
                  key_location_id: keyLocationId ?? undefined,
                  key_location: catEntry
                    ? {
                        id: catEntry.id,
                        name: catEntry.name,
                        slug: catEntry.slug,
                      }
                    : null,
                  key_location_note:
                    keyLocationNote != null &&
                    String(keyLocationNote).trim() !== ""
                      ? String(keyLocationNote).trim()
                      : null,
                  key_location_password: nextPassword || null,
                }
              : p
          )
        );
        addToast({ title: "Local das chaves atualizado.", type: "success" });
      } catch (err: any) {
        addToast({
          title:
            err?.response?.data?.message ??
            "Não foi possível atualizar a localização da chave.",
          type: "error",
        });
      } finally {
        setSavingKeyLocationId(null);
      }
    },
    [dateToExpires, access_token, addToast, keyLocationCatalog, properties]
  );

  useEffect(() => {
    if (shouldSyncSearchToFirstPage(debouncedSearch, filters.search)) return;

    async function loadApi() {
      if (!hasLoadedListOnceRef.current) {
        setLoadingApiProperties(true);
      } else {
        setListRefreshing(true);
      }

      try {
        const token = await refreshToken(dateToExpires, access_token);

        api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

        const response = await api.get("/api/adm/properties", {
          params: buildPropertiesApiParams(currentPage, {
            ...filters,
            search: debouncedSearch,
          }),
        });

        const listPayload = response.data.data.data;
        const list = Array.isArray(listPayload) ? listPayload : [];
        setProperties(
          list.map((item: Record<string, unknown>) =>
            toPropertyListRowFields(item)
          ) as IProperties[]
        );
        const rawLast = response.data.data.last_page;
        const parsedLast =
          rawLast == null || rawLast === "" ? 0 : Number(rawLast);
        setLastPage(
          Number.isFinite(parsedLast) && parsedLast >= 0 ? parsedLast : 0
        );
        setLoadApiError("");
      } catch (err: any) {
        const message =
          err?.response?.data?.message ?? "Erro ao carregar imóveis";

        if (err?.response?.status === 403) {
          setLoadApiError(message);
        }

        addToast({
          title: message,
          type: "error",
        });

        setLoadApiError(message);
      } finally {
        setLoadingApiProperties(false);
        setListRefreshing(false);
        hasLoadedListOnceRef.current = true;
      }
    }

    if (user) {
      loadApi();
    } else {
      history.push("/");
    }
  }, [
    history,
    access_token,
    dateToExpires,
    user,
    currentPage,
    filters,
    debouncedSearch,
    refresh,
    addToast,
  ]);

  const handleRemoveProperty = useCallback(async () => {
    setLoadingApiRequestToRemoveProperty(true);

    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

      await deletePropertyWithChildrenConfirm(
        api,
        propertyIdToRemove,
        removeRequiresChildrenConfirm
      );

      addToast({
        type: "success",
        title: "Imóvel removido",
      });

      setOpenModalConfirmRemoveProperty(false);
      setRemoveRequiresChildrenConfirm(false);
      handleRefresh();
    } catch (err: any) {
      const availableChildrenError = parseParentHasAvailableChildrenError(err);

      if (availableChildrenError && !removeRequiresChildrenConfirm) {
        setRemoveRequiresChildrenConfirm(true);
        setOpenModalConfirmRemoveProperty(true);
        return;
      }

      addToast({
        type: "error",
        title: err?.response?.data?.message ?? "Erro ao remover imóvel",
      });
    } finally {
      setLoadingApiRequestToRemoveProperty(false);
    }
  }, [
    dateToExpires,
    access_token,
    propertyIdToRemove,
    removeRequiresChildrenConfirm,
    addToast,
    handleRefresh,
  ]);

  const handleCloneProperty = useCallback(async () => {
    setLoadingApiRequestToCloneProperty(true);

    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

      const response = await api.post(
        `/api/adm/properties/clone/${propertyIdToClone}`
      );

      if (response.data?.success) {
        addToast({
          type: "success",
          title: `Imóvel ${propertyIdToClone} clonado. Novo código: ${response.data.data.id}`,
        });

        history.push(`/property/${response.data.data.id}`);
      }
    } catch (err: any) {
      addToast({
        type: "error",
        title: err?.response?.data?.message ?? "Erro ao clonar o imóvel",
      });
    } finally {
      setLoadingApiRequestToCloneProperty(false);
    }
  }, [dateToExpires, access_token, propertyIdToClone, addToast, history]);

  return (
    <Layout>
      <div className="w-full min-h-screen">
        {loadingApiProperties && !hasLoadedListOnceRef.current && <Loading />}

        <ModalConfirmRemoveProperty
          open={openModalConfirmRemoveProperty}
          isLoading={loadingApiRequestToRemoveProperty}
          message={
            removeRequiresChildrenConfirm
              ? "O imóvel possui filhos. Tem certeza que deseja excluí-lo?"
              : "Tem certeza que deseja deletar esse imóvel?"
          }
          functionToRemoveProperty={handleRemoveProperty}
          functionToCloseModal={() => {
            setOpenModalConfirmRemoveProperty(false);
            setRemoveRequiresChildrenConfirm(false);
          }}
        />

        <ModalConfirmCloneProperty
          open={openModalConfirmCloneProperty}
          propertyId={propertyIdToClone}
          isLoading={loadingApiRequestToCloneProperty}
          functionToCloneProperty={handleCloneProperty}
          functionToCloseModal={() => setOpenModalConfirmCloneProperty(false)}
        />

        {!loadingApiProperties && (
          <div className="mx-auto w-full max-w-7xl px-2 py-4 md:px-3 md:py-5">
            {!!loadApiError && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-6 py-10 text-center shadow-sm">
                <h2 className="text-xl font-medium text-red-600">
                  {loadApiError}
                </h2>
              </div>
            )}

            {!loadApiError && (
              <div>
                <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-slate-800">
                      {isPartnerBroker ? "Meus imóveis" : "Imóveis"}
                    </h3>
                    <p className="mt-1 text-sm text-slate-500">
                      Consulte, pesquise e filtre os imóveis disponíveis.
                    </p>
                  </div>

                  <Link
                    to="/create-property"
                    className="inline-flex items-center justify-center rounded-xl bg-orange-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-600"
                  >
                    Adicionar imóvel
                  </Link>
                </div>

                <PropertiesListFilters
                  searchTerm={searchTerm}
                  filters={filters}
                  showConsultantFilter={!isPartnerBroker}
                  onSearchChange={setSearchTerm}
                  onApplyAdvancedFilters={(advanced) =>
                    applyFilters({ ...filters, ...advanced })
                  }
                />

                <div className="relative mt-4">
                  {listRefreshing ? (
                    <div
                      className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/50 backdrop-blur-[0.5px]"
                      aria-busy="true"
                      aria-live="polite"
                    >
                      <span
                        className="inline-block h-9 w-9 animate-spin rounded-full border-2 border-orange-400 border-t-transparent"
                        role="status"
                        aria-label="Atualizando lista"
                      />
                    </div>
                  ) : null}
                  <table className="w-full table-fixed border-collapse text-[13px]">
                    <colgroup>
                      <col className="w-[7%]" />
                      <col className="w-[10%]" />
                      <col className="w-[18%]" />
                      <col className="w-[13%]" />
                      <col className="w-[8%]" />
                      <col className="w-[9%]" />
                      <col className="w-[12%]" />
                      <col className="w-[12%]" />
                      <col className="w-[11%]" />
                    </colgroup>
                    <thead>
                      <tr className="bg-slate-100 [&>th]:border-r [&>th]:border-slate-200 [&>th]:px-2 [&>th]:py-2.5 [&>th]:text-left [&>th]:text-xs [&>th]:font-semibold [&>th]:text-slate-700 [&>th:first-child]:rounded-tl-xl [&>th:first-child]:pl-3 [&>th:last-child]:rounded-tr-xl [&>th:last-child]:border-r-0 [&>th:last-child]:pr-3">
                        <th>
                          Código
                        </th>
                        <th>
                          Status
                        </th>
                        <th>
                          Endereço
                        </th>
                        <th>
                          Área / quartos
                        </th>
                        <th>
                          Aluguel
                        </th>
                        <th>
                          Tipo
                        </th>
                        <th>
                          Comissão
                        </th>
                        <th>
                          Local das chaves
                        </th>
                        <th>
                          Ações
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {properties.length > 0 ? (
                        properties.map((property, rowIndex) => {
                          const typeLabel = getPropertyTypeLabel(property);
                          const cepRaw = property.cep ?? property.zip_code;
                          const rowKeyId =
                            property.key_location &&
                            typeof property.key_location === "object" &&
                            property.key_location.id != null
                              ? Number(property.key_location.id)
                              : property.key_location_id != null
                                ? Number(property.key_location_id)
                                : null;
                          return (
                          <tr
                            key={property.id}
                            className={`border-t border-slate-100 transition hover:bg-slate-100 ${
                              rowIndex % 2 === 0 ? "bg-white" : "bg-slate-50"
                            }`}
                          >
                            <td className="overflow-hidden px-2 py-2 pl-3 text-[13px] font-medium text-slate-700">
                              #{property.id}
                            </td>
                            <td className="overflow-hidden px-2 py-2">
                              {property.is_draft ||
                              isDraftStatus(property.status_name) ||
                              isDraftStatus(property.status) ? (
                                <span className="inline-flex w-fit items-center whitespace-nowrap rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold leading-tight text-amber-800">
                                  {PROPERTY_STATUS_LABEL[PropertyStatus.PRE_REGISTERED]}
                                </span>
                              ) : property.is_rented ? (
                                <span className="inline-flex w-fit items-center whitespace-nowrap rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] font-semibold leading-tight text-slate-700">
                                  Inativo
                                </span>
                              ) : (
                                <span className="inline-flex w-fit items-center whitespace-nowrap rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold leading-tight text-emerald-800">
                                  Ativo
                                </span>
                              )}
                            </td>
                            <td className="min-w-0 overflow-hidden px-2 py-2 text-[13px] text-slate-600">
                              <div className="flex min-w-0 flex-col gap-0.5">
                                <span
                                  className="truncate font-semibold text-slate-700"
                                  title={buildPropertyAddressPrimaryLine(property)}
                                >
                                  {buildPropertyAddressPrimaryLine(property)}
                                </span>
                                <span className="text-xs text-slate-500">
                                  {formatCepLine(cepRaw)}
                                </span>
                              </div>
                            </td>
                            <td className="overflow-hidden px-2 py-2 text-[13px] text-slate-600">
                              <div className="flex flex-nowrap items-center gap-x-1.5 overflow-hidden text-slate-500">
                                <span className="inline-flex items-center gap-0.5">
                                  <BiShapeSquare className="shrink-0" size={15} />
                                  <span className="text-slate-600">
                                    {formatAreaForList(property.area)}
                                  </span>
                                </span>
                                <span className="inline-flex items-center gap-0.5">
                                  <BiBed className="shrink-0" size={15} />
                                  <span className="text-slate-600">
                                    {formatCountForList(
                                      property.rooms ?? undefined
                                    )}
                                  </span>
                                </span>
                                <span className="inline-flex items-center gap-0.5">
                                  <BiBath className="shrink-0" size={15} />
                                  <span className="text-slate-600">
                                    {formatCountForList(
                                      property.bathrooms ?? undefined
                                    )}
                                  </span>
                                </span>
                                <span className="inline-flex items-center gap-0.5">
                                  <BiCar className="shrink-0" size={15} />
                                  <span className="text-slate-600">
                                    {formatCountForList(
                                      property.garages ?? undefined
                                    )}
                                  </span>
                                </span>
                              </div>
                            </td>
                            <td className="overflow-hidden px-2 py-2 text-left text-[13px] font-semibold text-slate-700">
                              {formatValue(parseFloat(property.rent_value))}
                            </td>
                            <td className="overflow-hidden px-2 py-2 text-[13px] capitalize text-slate-600">
                              {typeLabel !== "" ? typeLabel : "—"}
                            </td>
                            <td className="overflow-hidden px-2 py-2 text-[12px] leading-snug text-slate-600">
                              {formatCommissionSummary(
                                commissionFormFromApi(
                                  property as unknown as Record<string, unknown>
                                )
                              )}
                            </td>
                            <td className="px-2 py-2 align-middle text-left text-[13px] text-slate-600">
                              {keyLocationCatalog.length === 0 ? (
                                <span className="text-xs text-slate-400">—</span>
                              ) : (
                                <PropertyKeyLocationTableSelect
                                  options={keyLocationCatalog}
                                  valueId={rowKeyId}
                                  valueNote={property.key_location_note}
                                  valuePassword={readListingKeyLocationPassword(
                                    property
                                  )}
                                  saving={savingKeyLocationId === property.id}
                                  onSave={(payload) =>
                                    handleSaveKeyLocation(property.id, payload)
                                  }
                                />
                              )}
                            </td>
                            <td className="overflow-hidden px-2 py-2 pr-3">
                              <div className="flex flex-nowrap items-center justify-end gap-0.5">
                                <Link
                                  to={`/property/${property.id}/details`}
                                  title="Ver detalhes"
                                  className="inline-flex shrink-0 rounded-lg p-1 text-sky-500 transition hover:bg-sky-50"
                                >
                                  <FiEye size={16} />
                                </Link>

                                <Link
                                  to={`/property/${property.id}`}
                                  title="Editar"
                                  className="inline-flex shrink-0 rounded-lg p-1 text-cyan-500 transition hover:bg-cyan-50"
                                >
                                  <BiEdit size={16} />
                                </Link>

                                <button
                                  type="button"
                                  title="Clonar"
                                  onClick={() => {
                                    setOpenModalConfirmCloneProperty(true);
                                    setPropertyIdToClone(property.id);
                                  }}
                                  className="inline-flex shrink-0 rounded-lg p-1 text-blue-900 transition hover:bg-blue-50"
                                >
                                  <BiCopy size={16} />
                                </button>

                                <button
                                  type="button"
                                  title="Excluir"
                                  onClick={() => {
                                    setRemoveRequiresChildrenConfirm(false);
                                    setOpenModalConfirmRemoveProperty(true);
                                    setPropertyIdToRemove(property.id);
                                  }}
                                  className="inline-flex shrink-0 rounded-lg p-1 text-red-500 transition hover:bg-red-50"
                                >
                                  <BiTrash size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            colSpan={9}
                            className="rounded-xl bg-slate-50 px-2 py-8 text-center text-sm text-slate-500"
                          >
                            {hasActivePropertyListFilters({
                              ...filters,
                              search: debouncedSearch,
                            })
                              ? "Nenhum imóvel encontrado para os filtros selecionados."
                              : "Nenhum imóvel encontrado."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <ListPagination
                  currentPage={currentPage}
                  totalPages={lastPage}
                  onPageChange={(page) => applyFilters(filters, page)}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Properties;